/**
 * useCascadeSlotEngine Hook
 *
 * Wires cascade slot engine services to the runtime store and game loop.
 * Manages lifecycle: initialize on play start, run game loop, cleanup on stop.
 *
 * Supports two modes:
 * - Local simulation (default): uses local SymbolGenerator, WinEvaluator, CascadeProcessor
 * - API mode (apiMode=true): calls integration API for each spin/cascade step
 *
 * Pure orchestration - delegates to engine services for logic.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSlotGameRuntimeStore } from '../store/slotGameRuntimeStore';
import { useLivePreviewStore } from '../store/livePreviewStore';
import { useSceneStore } from '../store/sceneStore';
import { useGameLoop } from './useGameLoop';
import type { CascadeSlotConfig, GridCell } from '@core/types/cascade-slot.types';
import {
  CascadeGamePhase as Phase,
  DEFAULT_CASCADE_SLOT_CONFIG,
} from '@core/types/cascade-slot.types';
import type { SymbolAnimationTrigger } from '@core/types/cascade-slot.types';
import type { BackendGameComponentData } from '@core/types/backend-game.types';
import { generateGrid } from '../services/cascade-slot/SymbolGenerator';
import { evaluateWins } from '../services/cascade-slot/WinEvaluator';
import { removeWinningSymbols, applyGravityWithTracking, clearAnimationFlags } from '../services/cascade-slot/CascadeProcessor';
import { collectMultipliers, calculateBonusWin } from '../services/cascade-slot/MultiplierResolver';
import { checkScatter } from '../services/cascade-slot/CascadeSlotEngine';
// API mode imports
import {
  initSession,
  requestBet,
  requestResult,
  requestBuyFeature,
  normalizeBetResponse,
} from '../services/backend-game/SlotApiService';
import {
  buildSymbolIdMap,
  convertApiGridToLocal,
  convertApiWinsToLocal,
  applyFallAnimationFlags,
} from '../services/backend-game/SymbolIdMapper';
import type { SymbolIdMap } from '../services/backend-game/SymbolIdMapper';
import { augmentSymbolsWithGolden, remapNormalSymbolCodes } from '../services/backend-game/GoldenSymbolGenerator';

interface UseCascadeSlotEngineOptions {
  config: CascadeSlotConfig | null;
  isPlaying: boolean;
}

/** Default initial board when no last activity is available (column-based, variable heights) */
const DEFAULT_INIT_BOARD: number[][] = [
  [31, 14, 11, 1],
  [13, 31, 3, 13, 4],
  [31, 14, 1, 14, 11],
  [12, 12, 13, 3, 11],
  [4, 12, 3, 4],
];

/** Resolve the bet index for the API. If currentBet isn't in betConfig, find closest match. */
function resolveBetIndex(betConfig: number[], currentBet: number): number {
  const exactIdx = betConfig.indexOf(currentBet);
  if (exactIdx >= 0) return exactIdx;
  // Fallback: find closest bet level
  let closestIdx = 0;
  let closestDiff = Math.abs(betConfig[0] - currentBet);
  for (let i = 1; i < betConfig.length; i++) {
    const diff = Math.abs(betConfig[i] - currentBet);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIdx = i;
    }
  }
  return closestIdx;
}

export function useCascadeSlotEngine({ config, isPlaying }: UseCascadeSlotEngineOptions) {
  const isInitializedRef = useRef(false);
  const processingRef = useRef(false);
  const finalizingRef = useRef(false);
  const lastBonusSpinRef = useRef(false);
  const symbolIdMapRef = useRef<SymbolIdMap | null>(null);
  const betInFlightRef = useRef(false); // Global mutex: prevents concurrent /bet calls
  const lastApiTotalWinRef = useRef(0); // Tracks last tw value to compute delta (API tw is cumulative across round)

  const store = useSlotGameRuntimeStore;

  // Initialize on play start
  useEffect(() => {
    if (isPlaying && config && !isInitializedRef.current) {
      store.getState().initialize(config);

      const apiMode = store.getState().apiMode;

      if (apiMode) {
        // API mode: init session, build symbol map, skip local grid generation
        // Find the VISIBLE backend game object (matches GameViewport's selection logic)
        const gameObjects = useSceneStore.getState().gameObjects;
        const backendGameObj = Object.values(gameObjects).find(
          (go) => go.visible !== false && go.components.some((c) => c.type === 'backend-game-config')
        );
        const backendGameComponent = backendGameObj?.components.find(
          (c) => c.type === 'backend-game-config'
        );
        const backendData = backendGameComponent?.data as BackendGameComponentData | undefined;

        if (backendData?.gameId && backendData?.backendConfig?.symbols) {
          const augmentedSymbols = augmentSymbolsWithGolden(
            backendData.backendConfig.symbols,
            backendData.backendConfig.mechanics
          );
          const remappedSymbols = remapNormalSymbolCodes(augmentedSymbols);
          symbolIdMapRef.current = buildSymbolIdMap(remappedSymbols);

          // Async session init
          initSession(backendData.gameId)
            .then((session) => {
              store.getState().setApiSession(session);
              store.getState().syncBalanceFromApi(session.balance);
              if (session.betConfig.length > 0) {
                store.getState().updateConfig({
                  ...store.getState().config,
                  betLevels: session.betConfig,
                });
                store.getState().setBetLevel(session.betConfig[0]);
              }

              // Populate initial grid from last activity or default board
              // Clear animation flags so the board appears instantly (no fall-in)
              const cfg = store.getState().config;
              if (cfg && symbolIdMapRef.current) {
                const lastReel = session.lastActivity?.crl
                  ?? session.lastActivity?.current_reel
                  ?? null;
                const initBoard = lastReel && lastReel.length > 0
                  ? lastReel
                  : DEFAULT_INIT_BOARD;
                const initialGrid = convertApiGridToLocal(
                  initBoard,
                  symbolIdMapRef.current,
                  cfg
                );

                // Fill any empty cells (unmapped IDs or missing data) with random symbols
                // Convert empty valid cells to null so generateGrid can fill them
                const patchedGrid: (GridCell | null)[][] = initialGrid.map((row, ri) =>
                  row.map((cell, ci) => {
                    if (cell.symbolId === '') {
                      // Check if this cell is within valid reel bounds
                      const colHeight = cfg.reelHeights?.[ci] ?? cfg.rows;
                      const startRow = cfg.rows - colHeight;
                      if (ri >= startRow) {
                        return null; // Mark for filling
                      }
                    }
                    return cell;
                  })
                );
                const filledGrid = generateGrid(cfg, patchedGrid);

                const staticGrid = filledGrid.map(row =>
                  row.map(cell => ({ ...cell, isNew: false, isFalling: false, fallDistance: 0 }))
                );
                store.getState().setGrid(staticGrid);
              }
            })
            .catch((err) => {
              console.warn('[CascadeSlotEngine] Live API unavailable, falling back to local simulation:', (err as Error).message);
              // Disable API mode and initialize with local grid
              store.getState().setApiMode(false);
              store.getState().setApiError(null);
              const localGrid = generateGrid(config);
              store.getState().setGrid(localGrid);
            });
        }

        // Start with empty grid in API mode until session resolves
        store.getState().setGrid([]);
      } else {
        // Local mode: generate initial grid
        const initialGrid = generateGrid(config);
        store.getState().setGrid(initialGrid);
      }

      isInitializedRef.current = true;
    } else if (isPlaying && config && isInitializedRef.current) {
      // Config changed during play (e.g. mode switch) - only update config, preserve state
      // In API mode, preserve session betLevels (don't overwrite with local config)
      const session = store.getState().apiSession;
      if (store.getState().apiMode && session && session.betConfig.length > 0) {
        store.getState().updateConfig({
          ...config,
          betLevels: session.betConfig,
        });
      } else {
        store.getState().updateConfig(config);
      }
    } else if (!isPlaying && isInitializedRef.current) {
      store.getState().reset();
      symbolIdMapRef.current = null;
      isInitializedRef.current = false;
      processingRef.current = false;
      finalizingRef.current = false;
      lastBonusSpinRef.current = false;
      betInFlightRef.current = false;
    }
  }, [isPlaying, config]);

  // Finalize a spin (no more cascades)
  const finalizeSpin = useCallback(async (cfg: CascadeSlotConfig) => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    try {
    const state = store.getState();

    if (state.apiMode && state.apiSession) {
      const wasFreeSpinsActive = state.freeSpinsActive;

      // Read pending result BEFORE clearing — callers no longer clear it.
      const pending = store.getState().pendingBetResult;
      store.getState().setPendingBetResult(null);

      if (wasFreeSpinsActive) {
        // During free spins: DON'T call /result between individual spins.
        // /result will be called once in FreeSpinsOutro when all spins are done.

        // Sync free spin display count from API response
        if (pending && pending.freeSpinCount >= 0) {
          store.getState().setFreeSpins(pending.freeSpinCount);
        }

        // API says bonus is over (endRound=true + no more free spins)
        if (pending && pending.endRound && pending.freeSpinCount === 0) {
          store.getState().resetCascade();
          store.getState().setGamePhase(Phase.FreeSpinsOutro);
          lastBonusSpinRef.current = false;
          return;
        }

        // Otherwise continue free spin loop
        store.getState().resetCascade();
        store.getState().setGamePhase(Phase.FreeSpinning);
        lastBonusSpinRef.current = false;
        return;
      }

      // Check if bonus is about to start (from buy-feature or bet with freeSpinCount).
      // Enter free spins WITHOUT calling /result — avoid settlement conflict.
      const hasBonusTrigger = lastBonusSpinRef.current
        || (pending != null && pending.freeSpinCount > 0);
      if (hasBonusTrigger) {
        const freeSpinCount = pending?.freeSpinCount
          || cfg.freeSpins.spinsAwarded
          || 10;
        store.getState().setFreeSpins(freeSpinCount);
        store.getState().resetCascade();
        store.getState().setGamePhase(Phase.FreeSpinsIntro);
        lastBonusSpinRef.current = false;
        return;
      }

      // Normal spin — call /result to settle the round
      try {
        const resultRes = await requestResult(state.apiSession.token);
        store.getState().syncBalanceFromApi(resultRes.data.balance);

        // Check bonus_event for free spins trigger from /result
        const bonus = resultRes.data.bonus_event;
        if (bonus && bonus.rounds > 0 && bonus.round_used < bonus.rounds) {
          store.getState().setFreeSpins(bonus.rounds - bonus.round_used);
          store.getState().resetCascade();
          store.getState().setGamePhase(Phase.FreeSpinsIntro);
          return;
        }
      } catch (err) {
        console.error('[finalizeSpin] /result error:', (err as Error).message);
        store.getState().setApiError((err as Error).message);
      }

      // Normal spin complete
      store.getState().creditWin();
      store.getState().resetCascade();
      store.getState().resetMultiplier();
      store.getState().setGamePhase(Phase.Idle);
    } else {
      // LOCAL MODE: existing finalizeSpin logic
      const grid = state.grid;

      // Check BN multipliers
      const { totalMultiplier } = collectMultipliers(grid);
      if (totalMultiplier > 0 && state.tumbleWin > 0) {
        const bonusWin = calculateBonusWin(state.tumbleWin, totalMultiplier);
        store.getState().addWin(bonusWin);
        store.getState().setCurrentSpinWin(store.getState().currentSpinWin + bonusWin);
      }
      store.getState().addMultiplier(totalMultiplier);

      // Check scatter (pass current bet for scatter pay calculation)
      const scatterResult = checkScatter(grid, state.freeSpinsActive, cfg, state.currentBet);

      // Add scatter wins if any
      if (scatterResult.scatterWin > 0) {
        store.getState().addWin(scatterResult.scatterWin);
        store.getState().setCurrentSpinWin(store.getState().currentSpinWin + scatterResult.scatterWin);
        store.getState().addScatterWin(scatterResult.scatterWin);
      }

      // Max win cap
      if (cfg.maxWin) {
        const currentTotalWin = store.getState().totalSpinWin;
        if (currentTotalWin > cfg.maxWin) {
          const overshoot = currentTotalWin - cfg.maxWin;
          store.getState().addWin(-overshoot);
        }
      }

      if (scatterResult.triggered) {
        if (state.freeSpinsActive) {
          const current = store.getState().freeSpinsRemaining;
          store.getState().setFreeSpins(current + scatterResult.freeSpins);
        } else {
          store.getState().creditWin();
          store.getState().resetCascade();
          store.getState().resetMultiplier();
          store.getState().resetWinCounters();
          store.getState().setFreeSpins(scatterResult.freeSpins);
          store.getState().setGamePhase(Phase.FreeSpinsIntro);
          return;
        }
      }

      if (state.freeSpinsActive) {
        store.getState().resetCascade();
        store.getState().setGamePhase(Phase.FreeSpinning);
      } else {
        store.getState().creditWin();
        store.getState().resetCascade();
        store.getState().setGamePhase(Phase.Idle);
      }
    }
    } finally {
      finalizingRef.current = false;
    }
  }, []);

  // Game phase state machine - driven by tick
  const processGamePhase = useCallback((deltaSec: number) => {
    if (!isInitializedRef.current || processingRef.current || finalizingRef.current) return;

    const state = store.getState();
    const { gamePhase, config: cfg } = state;
    if (!cfg) return;

    const deltaMs = deltaSec * 1000;
    store.getState().tick(deltaMs);

    const turboMult = state.turbo ? cfg.turboMultiplier : 1;
    const skipAnim = store.getState().skipAnimations;

    switch (gamePhase) {
      case Phase.Idle: {
        if (store.getState().skipAnimations) {
          store.getState().setSkipAnimations(false);
        }
        // Ensure bet-in-flight mutex is cleared when returning to Idle
        // (safety reset for cases where async cascade calls didn't complete)
        betInFlightRef.current = false;
        processingRef.current = false;
        finalizingRef.current = false;
        // Auto-spin: trigger next spin automatically
        if (state.autoSpinActive && state.autoSpinRemaining > 0) {
          if (state.apiMode || state.balance >= state.currentBet) {
            store.getState().requestSpin();
          }
        }
        break;
      }

      case Phase.Spinning: {
        // Reset per-spin win counter at the start of each spin (including free spins)
        if (store.getState().currentSpinWin !== 0) {
          store.getState().resetCurrentSpinWin();
        }
        // Reset cumulative tw tracker for this new round
        lastApiTotalWinRef.current = 0;
        if (state.apiMode && state.apiSession) {
          // ===== API MODE: Spinning =====
          if (betInFlightRef.current) break; // Prevent concurrent API calls
          processingRef.current = true;
          betInFlightRef.current = true;
          store.getState().setApiLoading(true);
          store.getState().setApiError(null);

          (async () => {
            try {
              const session = store.getState().apiSession!;
              // API expects bet INDEX (position in betConfig[]), not the actual value
              const currentBet = store.getState().currentBet;
              const betIndex = resolveBetIndex(session.betConfig, currentBet);

              // Detect buy feature: mode changed AND not already in free spins
              // (freeSpinsActive prevents re-triggering during bonus spins)
              const isBuyFeature = !state.freeSpinsActive
                && state.previousMode !== state.activeMode
                && state.activeMode > 0;

              let betData;
              if (isBuyFeature) {
                const response = await requestBuyFeature(session.token, betIndex, state.activeMode);
                betData = response.data;
              } else {
                const response = await requestBet(session.token, betIndex);
                betData = response.data;
              }

              const normalized = normalizeBetResponse(betData);
              const gridCells = convertApiGridToLocal(
                normalized.currentReel,
                symbolIdMapRef.current!,
                cfg
              );

              // Mark all cells for fall-in animation
              const animatedGrid = gridCells.map((row, ri) =>
                row.map((cell, ci) => ({ ...cell, isNew: true, columnIndex: ci }))
              );

              // If user force-stopped while bet was in flight, settle immediately
              const currentPhase = store.getState().gamePhase;
              if (currentPhase === Phase.Idle) {
                console.warn('[CascadeSlotEngine] Bet returned after force-stop — auto-settling round');
                store.getState().syncBalanceFromApi(normalized.balance);
                store.getState().setApiLoading(false);
                try {
                  const settleSession = store.getState().apiSession;
                  if (settleSession) {
                    const resultRes = await requestResult(settleSession.token);
                    store.getState().syncBalanceFromApi(resultRes.data.balance);
                  }
                } catch (settleErr) {
                  console.error('[CascadeSlotEngine] Auto-settle /result failed:', (settleErr as Error).message);
                }
                return;
              }

              store.getState().setPendingBetResult(normalized);
              // Buy-feature ALWAYS triggers bonus — don't rely on isBonusSpin flag.
              // For normal spins, also detect bonus from freeSpinCount.
              if (isBuyFeature) {
                lastBonusSpinRef.current = true;
              } else {
                lastBonusSpinRef.current = normalized.isBonusSpin || normalized.freeSpinCount > 0;
              }
              // Store result grid for reel animation, then start reel spinning
              store.getState().setReelSpinResultGrid(animatedGrid);
              store.getState().setReelSpinComplete(false);
              store.getState().syncBalanceFromApi(normalized.balance);
              store.getState().setApiLoading(false);
              store.getState().setGamePhase(Phase.ReelSpinning);
            } catch (err) {
              console.error('[Spinning] bet error:', (err as Error).message);
              store.getState().setApiError((err as Error).message);
              store.getState().setApiLoading(false);
              store.getState().setGamePhase(Phase.Idle);
            } finally {
              betInFlightRef.current = false;
              processingRef.current = false;
            }
          })();
        } else {
          // ===== LOCAL MODE: Spinning =====
          if (!state.freeSpinsActive) {
            store.getState().deductBet();
          }
          const isFreeGame = state.freeSpinsActive;
          const grid = generateGrid(cfg, undefined, isFreeGame);
          const animatedGrid = grid.map((row, ri) =>
            row.map((cell, ci) => ({ ...cell, isNew: true, columnIndex: ci }))
          );
          // Store result grid for reel animation, then start reel spinning
          store.getState().setReelSpinResultGrid(animatedGrid);
          store.getState().setReelSpinComplete(false);
          store.getState().setGamePhase(Phase.ReelSpinning);
        }
        break;
      }

      case Phase.ReelSpinning: {
        // Wait for overlay to signal all reels have stopped
        if (store.getState().reelSpinComplete) {
          const resultGrid = store.getState().reelSpinResultGrid;
          if (resultGrid) {
            const cleanGrid = clearAnimationFlags(resultGrid);
            store.getState().setGrid(cleanGrid);
            store.getState().setReelSpinResultGrid(null);
          }
          store.getState().setGamePhase(Phase.CascadeFill);
        }
        // Safety timeout: 10s max
        if (store.getState().stateTimer > 10000) {
          store.getState().setReelSpinComplete(true);
        }
        break;
      }

      case Phase.Evaluating: {
        // Clear skipAnimations at the cascade-step boundary so the NEXT
        // cascade step (next /bet response) animates normally.
        if (store.getState().skipAnimations) {
          store.getState().setSkipAnimations(false);
        }
        if (state.apiMode) {
          // ===== API MODE: Evaluating =====
          processingRef.current = true;
          try {
            const pending = store.getState().pendingBetResult;
            if (!pending) {
              finalizeSpin(cfg);
              break;
            }

            const wins = convertApiWinsToLocal(
              pending.winningPositions,
              pending.winReward,
              symbolIdMapRef.current!,
              store.getState().grid
            );

            if (wins.length > 0) {
              // API sw is cumulative per-spin (resets each free spin), tw is cumulative per-round.
              // Use tw delta for winAmount (session total), sw directly for currentSpinWin (per-spin).
              const twDelta = pending.totalWin - lastApiTotalWinRef.current;
              lastApiTotalWinRef.current = pending.totalWin;
              store.getState().addWin(twDelta > 0 ? twDelta : pending.spinWin);
              // Set currentSpinWin directly from sw (per-spin cumulative, auto-resets per free spin)
              store.getState().setCurrentSpinWin(pending.spinWin);
              store.getState().incrementCascade();
              store.getState().setLastWins(wins);

              // Mark winning cells
              const grid = store.getState().grid;
              const markedGrid = grid.map((row, ri) =>
                row.map((cell, ci) => ({ ...cell, columnIndex: ci }))
              );
              for (const win of wins) {
                for (const pos of win.positions) {
                  if (markedGrid[pos.row]?.[pos.col]) {
                    markedGrid[pos.row][pos.col] = {
                      ...markedGrid[pos.row][pos.col],
                      isWinning: true,
                      animationTrigger: 'match' as SymbolAnimationTrigger,
                      columnIndex: pos.col,
                    };
                  }
                }
              }
              store.getState().setGrid(markedGrid);
              store.getState().setGamePhase(Phase.WinPresenting);
            } else {
              // No wins — check free spins trigger, cascade continuation, or finalize
              if (
                !pending.endRound &&
                pending.freeSpinCount > 0 &&
                pending.mode === 0 &&
                !state.freeSpinsActive
              ) {
                // Free spins trigger from bet response
                store.getState().setPendingBetResult(null);
                store.getState().setFreeSpins(pending.freeSpinCount);
                store.getState().setGamePhase(Phase.FreeSpinsIntro);
              } else {
                // No wins — cascade chain ended for this spin. Finalize.
                // finalizeSpin handles: endRound → settle, freeSpins → FreeSpinning, normal → Idle.
                // The next /bet will be called when Spinning re-enters via FreeSpinning or auto-spin.
                finalizeSpin(cfg);
              }
            }
          } finally {
            processingRef.current = false;
          }
        } else {
          // ===== LOCAL MODE: Evaluating =====
          processingRef.current = true;
          try {
            const grid = store.getState().grid;
            const wins = evaluateWins(grid, cfg);

            if (wins.length > 0) {
              const stepPayout = wins.reduce((sum, w) => sum + w.payout, 0);
              store.getState().addWin(stepPayout);
              // Local mode: accumulate currentSpinWin manually
              store.getState().setCurrentSpinWin(store.getState().currentSpinWin + stepPayout);
              store.getState().incrementCascade();
              store.getState().setLastWins(wins);

              const markedGrid = grid.map((row, ri) => row.map((cell, ci) => ({ ...cell, columnIndex: ci })));
              for (const win of wins) {
                for (const pos of win.positions) {
                  markedGrid[pos.row][pos.col] = { ...markedGrid[pos.row][pos.col], isWinning: true, animationTrigger: 'match' as SymbolAnimationTrigger, columnIndex: pos.col };
                }
              }
              store.getState().setGrid(markedGrid);
              store.getState().setGamePhase(Phase.WinPresenting);
            } else {
              finalizeSpin(cfg);
            }
          } finally {
            processingRef.current = false;
          }
        }
        break;
      }

      case Phase.WinPresenting: {
        const elapsed = store.getState().stateTimer;
        if (skipAnim || elapsed >= cfg.winPresentDuration * 1000 / turboMult) {
          store.getState().setGamePhase(Phase.CascadeRemove);
        }
        break;
      }

      case Phase.CascadeRemove: {
        const grid = store.getState().grid;
        const elapsed = store.getState().stateTimer;
        let hasRemovingCells = grid.some(row => row.some(cell => cell.isRemoving));

        if (!hasRemovingCells) {
          // First tick: mark winning cells as isRemoving for animation
          const markedGrid = grid.map(row =>
            row.map(cell => cell.isWinning
              ? { ...cell, isWinning: false, isRemoving: true }
              : { ...cell }
            )
          );
          store.getState().setGrid(markedGrid);
          hasRemovingCells = markedGrid.some(row => row.some(cell => cell.isRemoving));

          if (!hasRemovingCells) {
            // Safety: no cells to remove — skip removal and proceed
            if (state.apiMode && state.apiSession) {
              const pending = store.getState().pendingBetResult;
              if (pending && !pending.endRound && !betInFlightRef.current) {
                // Cascade continues — fetch next step
                processingRef.current = true;
                betInFlightRef.current = true;
                store.getState().setApiLoading(true);
                (async () => {
                  try {
                    const session = store.getState().apiSession!;
                    const currentBet = store.getState().currentBet;
                    const betIndex = resolveBetIndex(session.betConfig, currentBet);
                    const betResponse = await requestBet(session.token, betIndex);
                    const normalized = normalizeBetResponse(betResponse.data);
                    const newGrid = convertApiGridToLocal(
                      normalized.currentReel,
                      symbolIdMapRef.current!,
                      cfg
                    );
                    const animatedGrid = newGrid.map((row, ri) =>
                      row.map((cell, ci) => ({ ...cell, isNew: true, columnIndex: ci }))
                    );
                    store.getState().setPendingBetResult(normalized);
                    lastBonusSpinRef.current = lastBonusSpinRef.current || normalized.isBonusSpin || normalized.freeSpinCount > 0;
                    store.getState().setGrid(animatedGrid);
                    store.getState().syncBalanceFromApi(normalized.balance);
                    store.getState().setApiLoading(false);
                    store.getState().setGamePhase(Phase.CascadeFill);
                  } catch (err) {
                    store.getState().setApiError((err as Error).message);
                    store.getState().setApiLoading(false);
                    store.getState().setGamePhase(Phase.Idle);
                  } finally {
                    betInFlightRef.current = false;
                    processingRef.current = false;
                  }
                })();
              } else {
                // endRound or no pending or bet in flight — finalize
                finalizeSpin(cfg);
              }
            } else {
              finalizeSpin(cfg);
            }
            break;
          }
        }

        if (hasRemovingCells && (skipAnim || elapsed >= cfg.cascadeRemoveDuration * 1000 / turboMult)) {
          if (state.apiMode && state.apiSession) {
            // ===== API MODE: After removal, fetch next cascade step =====
            const pending = store.getState().pendingBetResult;
            if (pending && !pending.endRound && !betInFlightRef.current) {
              processingRef.current = true;
              betInFlightRef.current = true;
              store.getState().setApiLoading(true);

              (async () => {
                try {
                  const session = store.getState().apiSession!;
                  const currentBet = store.getState().currentBet;
                  const betIndex = resolveBetIndex(session.betConfig, currentBet);
                  const betResponse = await requestBet(session.token, betIndex);
                  const normalized = normalizeBetResponse(betResponse.data);
                  const newGrid = convertApiGridToLocal(
                    normalized.currentReel,
                    symbolIdMapRef.current!,
                    cfg
                  );

                  // Apply fall animation flags
                  const oldCleanGrid = grid.map(row =>
                    row.map(cell => cell.isRemoving ? { ...cell, symbolId: '' } : cell)
                  );
                  const animatedGrid = applyFallAnimationFlags(oldCleanGrid, newGrid);

                  store.getState().setPendingBetResult(normalized);
                  lastBonusSpinRef.current = lastBonusSpinRef.current || normalized.isBonusSpin || normalized.freeSpinCount > 0;
                  store.getState().setGrid(animatedGrid);
                  store.getState().syncBalanceFromApi(normalized.balance);
                  store.getState().setApiLoading(false);
                  store.getState().setGamePhase(Phase.CascadeGravity);
                } catch (err) {
                  store.getState().setApiError((err as Error).message);
                  store.getState().setApiLoading(false);
                  store.getState().setGamePhase(Phase.Idle);
                } finally {
                  betInFlightRef.current = false;
                  processingRef.current = false;
                }
              })();
            } else {
              // endRound or no pending — finalize (don't clear pending; finalizeSpin reads it)
              finalizeSpin(cfg);
            }
          } else {
            // ===== LOCAL MODE: gravity + fill =====
            const cleanGrid: (GridCell | null)[][] = grid.map(row =>
              row.map(cell => cell.isRemoving ? null : { ...cell, isRemoving: false })
            );
            const { result: afterGravity, fallMap } = applyGravityWithTracking(cleanGrid, cfg.cascadeDirection, cfg.reelHeights);
            const filledGrid = generateGrid(cfg, afterGravity, store.getState().freeSpinsActive);
            for (const [key, dist] of Object.entries(fallMap)) {
              const [r, c] = key.split('-').map(Number);
              if (filledGrid[r] && filledGrid[r][c]) {
                filledGrid[r][c] = { ...filledGrid[r][c], isFalling: true, fallDistance: dist, columnIndex: c };
              }
            }
            store.getState().setGrid(filledGrid);
            store.getState().setGamePhase(Phase.CascadeGravity);
          }
        }
        break;
      }

      case Phase.CascadeGravity: {
        // Clear skipAnimations — this phase shows NEW cascade data that should animate
        if (store.getState().skipAnimations) {
          store.getState().setSkipAnimations(false);
        }
        const elapsed = store.getState().stateTimer;
        const maxCol = cfg.columns - 1;
        const totalAnimWait = (
          Math.max(cfg.cascadeGravityDuration, cfg.cascadeFillDuration) * 1000
          + maxCol * cfg.cascadeColumnDelay
        ) / turboMult;

        if (elapsed >= totalAnimWait) {
          const grid = store.getState().grid;
          const cleanGrid = clearAnimationFlags(grid);
          store.getState().setGrid(cleanGrid);
          store.getState().setGamePhase(Phase.Evaluating);
        }
        break;
      }

      case Phase.CascadeFill: {
        // Clear skipAnimations — this phase shows NEW cascade data that should animate
        if (store.getState().skipAnimations) {
          store.getState().setSkipAnimations(false);
        }
        const elapsed = store.getState().stateTimer;
        const maxCol = cfg.columns - 1;
        const fillWait = (cfg.cascadeFillDuration * 1000 + maxCol * cfg.cascadeColumnDelay) / turboMult;

        if (elapsed >= fillWait) {
          const grid = store.getState().grid;
          const cleanGrid = clearAnimationFlags(grid);
          store.getState().setGrid(cleanGrid);
          store.getState().setGamePhase(Phase.Evaluating);
        }
        break;
      }

      case Phase.FreeSpinsIntro: {
        const elapsed = store.getState().stateTimer;
        if (skipAnim || elapsed >= 1500 / turboMult) {
          store.getState().setGamePhase(Phase.FreeSpinning);
        }
        break;
      }

      case Phase.FreeSpinning: {
        const remaining = store.getState().freeSpinsRemaining;
        if (remaining > 0) {
          // In API mode, don't decrement — finalizeSpin syncs from the API's frm value.
          // In local mode, decrement the local counter as usual.
          if (!state.apiMode) {
            store.getState().setFreeSpins(remaining - 1);
          }
          store.getState().setGamePhase(Phase.Spinning);
        } else {
          store.getState().setGamePhase(Phase.FreeSpinsOutro);
        }
        break;
      }

      case Phase.FreeSpinsOutro: {
        const elapsed = store.getState().stateTimer;
        if (skipAnim || elapsed >= 2000 / turboMult) {
          if (state.apiMode && state.apiSession) {
            // API mode: call /result to settle free spins round
            processingRef.current = true;
            (async () => {
              try {
                const resultRes = await requestResult(state.apiSession!.token);
                store.getState().syncBalanceFromApi(resultRes.data.balance);
              } catch (err) {
                store.getState().setApiError((err as Error).message);
              } finally {
                processingRef.current = false;
              }

              store.getState().setFreeSpins(0);
              store.getState().creditWin();
              store.getState().resetCascade();
              store.getState().resetMultiplier();
              const prevMode = store.getState().previousMode;
              store.getState().setActiveMode(prevMode);
              store.getState().setGamePhase(Phase.Idle);
            })();
          } else {
            store.getState().setFreeSpins(0);
            store.getState().creditWin();
            store.getState().resetCascade();
            store.getState().resetMultiplier();
            const prevMode = store.getState().previousMode;
            store.getState().setActiveMode(prevMode);
            store.getState().setGamePhase(Phase.Idle);
          }
        }
        break;
      }
    }
  }, []);

  // Wire to game loop
  useGameLoop({
    onUpdate: (deltaTime) => {
      if (!isInitializedRef.current) return;
      processGamePhase(deltaTime);
    },
  });

  return {
    isInitialized: isInitializedRef.current,
  };
}
